import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import './SocialLinks.css';

// i18n translations
const translations = {
    en: {
        name: "Fahad Al-Shwihani",
        tagline: "Let's Connect",
        phone: "Phone Number",
        phoneDesc: "Give me a call",
        whatsapp: "WhatsApp",
        whatsappDesc: "Message me instantly",
        linkedin: "LinkedIn",
        linkedinDesc: "Professional network",
        email: "Email",
        emailDesc: "Send me an email",
        website: "Website",
        websiteDesc: "Visit my portfolio",
        github: "GitHub",
        githubDesc: "Check out my code"
    },
    ar: {
        name: "فهـد الشويحـانـي",
        tagline: "يسعدنـي تواصلـك",
        phone: "رقـم الجـوال",
        phoneDesc: "اتصـل علي مباشـرة",
        whatsapp: "واتســاب",
        whatsappDesc: "راسلنـي على الواتسـاب",
        linkedin: "لينكـد إن",
        linkedinDesc: "حسابـي المهنـي",
        email: "البريــد الإلكتـرونـي",
        emailDesc: "راسلنـي عبـر الإيميـل",
        website: "الموقـع الإلكتـرونـي",
        websiteDesc: "تصفـح أعمالـي",
        github: "جيـت هـب",
        githubDesc: "تصفـح أكوادي ومشاريعـي"
    }
};

// ColorBends Component
const MAX_COLORS = 8;

const frag = `
#define MAX_COLORS ${MAX_COLORS}
uniform vec2 uCanvas;
uniform float uTime;
uniform float uSpeed;
uniform vec2 uRot;
uniform int uColorCount;
uniform vec3 uColors[MAX_COLORS];
uniform int uTransparent;
uniform float uScale;
uniform float uFrequency;
uniform float uWarpStrength;
uniform vec2 uPointer; // in NDC [-1,1]
uniform float uMouseInfluence;
uniform float uParallax;
uniform float uNoise;
varying vec2 vUv;

void main() {
  float t = uTime * uSpeed;
  vec2 p = vUv * 2.0 - 1.0;
  p += uPointer * uParallax * 0.1;
  vec2 rp = vec2(p.x * uRot.x - p.y * uRot.y, p.x * uRot.y + p.y * uRot.x);
  vec2 q = vec2(rp.x * (uCanvas.x / uCanvas.y), rp.y);
  q /= max(uScale, 0.0001);
  q /= 0.5 + 0.2 * dot(q, q);
  q += 0.2 * cos(t) - 7.56;
  vec2 toward = (uPointer - rp);
  q += toward * uMouseInfluence * 0.2;

    vec3 col = vec3(0.0);
    float a = 1.0;

    if (uColorCount > 0) {
      vec2 s = q;
      vec3 sumCol = vec3(0.0);
      float cover = 0.0;
      for (int i = 0; i < MAX_COLORS; ++i) {
            if (i >= uColorCount) break;
            s -= 0.01;
            vec2 r = sin(1.5 * (s.yx * uFrequency) + 2.0 * cos(s * uFrequency));
            float m0 = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(i)) / 4.0);
            float kBelow = clamp(uWarpStrength, 0.0, 1.0);
            float kMix = pow(kBelow, 0.3); // strong response across 0..1
            float gain = 1.0 + max(uWarpStrength - 1.0, 0.0); // allow >1 to amplify displacement
            vec2 disp = (r - s) * kBelow;
            vec2 warped = s + disp * gain;
            float m1 = length(warped + sin(5.0 * warped.y * uFrequency - 3.0 * t + float(i)) / 4.0);
            float m = mix(m0, m1, kMix);
            float w = 1.0 - exp(-6.0 / exp(6.0 * m));
            sumCol += uColors[i] * w;
            cover = max(cover, w);
      }
      col = clamp(sumCol, 0.0, 1.0);
      a = uTransparent > 0 ? cover : 1.0;
    } else {
        vec2 s = q;
        for (int k = 0; k < 3; ++k) {
            s -= 0.01;
            vec2 r = sin(1.5 * (s.yx * uFrequency) + 2.0 * cos(s * uFrequency));
            float m0 = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(k)) / 4.0);
            float kBelow = clamp(uWarpStrength, 0.0, 1.0);
            float kMix = pow(kBelow, 0.3);
            float gain = 1.0 + max(uWarpStrength - 1.0, 0.0);
            vec2 disp = (r - s) * kBelow;
            vec2 warped = s + disp * gain;
            float m1 = length(warped + sin(5.0 * warped.y * uFrequency - 3.0 * t + float(k)) / 4.0);
            float m = mix(m0, m1, kMix);
            col[k] = 1.0 - exp(-6.0 / exp(6.0 * m));
        }
        a = uTransparent > 0 ? max(max(col.r, col.g), col.b) : 1.0;
    }

    if (uNoise > 0.0001) {
      float n = fract(sin(dot(gl_FragCoord.xy + vec2(uTime), vec2(12.9898, 78.233))) * 43758.5453123);
      col += (n - 0.5) * uNoise;
      col = clamp(col, 0.0, 1.0);
    }

    vec3 rgb = (uTransparent > 0) ? col * a : col;
    gl_FragColor = vec4(rgb, a);
}
`;

const vert = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

function ColorBends({
    colors = ['#080758ff', '#590672ff', '#2109a5ff'],
    speed = 0.2,
    rotation = 45,
    autoRotate = 0,
    scale = 1,
    frequency = 1,
    warpStrength = 1,
    mouseInfluence = 1,
    parallax = 0.5,
    noise = 0.1,
    transparent = true
}) {
    const containerRef = useRef(null);
    const rendererRef = useRef(null);
    const rafRef = useRef(null);
    const materialRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const rotationRef = useRef(rotation);
    const autoRotateRef = useRef(autoRotate);
    const pointerTargetRef = useRef(new THREE.Vector2(0, 0));
    const pointerCurrentRef = useRef(new THREE.Vector2(0, 0));
    const pointerSmoothRef = useRef(8);

    useEffect(() => {
        const container = containerRef.current;
        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

        const geometry = new THREE.PlaneGeometry(2, 2);
        const uColorsArray = Array.from({ length: MAX_COLORS }, () => new THREE.Vector3(0, 0, 0));

        const material = new THREE.ShaderMaterial({
            vertexShader: vert,
            fragmentShader: frag,
            uniforms: {
                uCanvas: { value: new THREE.Vector2(1, 1) },
                uTime: { value: 0 },
                uSpeed: { value: 0.2 },
                uRot: { value: new THREE.Vector2(1, 0) },
                uColorCount: { value: 0 },
                uColors: { value: uColorsArray },
                uTransparent: { value: 1 },
                uScale: { value: 1 },
                uFrequency: { value: 1 },
                uWarpStrength: { value: 1 },
                uPointer: { value: new THREE.Vector2(0, 0) },
                uMouseInfluence: { value: 0.5 },
                uParallax: { value: 0.3 },
                uNoise: { value: 0.05 }
            },
            premultipliedAlpha: true,
            transparent: true
        });
        materialRef.current = material;

        rotationRef.current = rotation;
        autoRotateRef.current = autoRotate;
        material.uniforms.uSpeed.value = speed;
        material.uniforms.uScale.value = scale;
        material.uniforms.uFrequency.value = frequency;
        material.uniforms.uWarpStrength.value = warpStrength;
        material.uniforms.uMouseInfluence.value = mouseInfluence;
        material.uniforms.uParallax.value = parallax;
        material.uniforms.uNoise.value = noise;

        const toVec3 = hex => {
            const h = hex.replace('#', '').trim();
            const v = h.length === 3
                ? [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16)]
                : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
            return new THREE.Vector3(v[0] / 255, v[1] / 255, v[2] / 255);
        };

        const arr = colors.map(toVec3);
        for (let i = 0; i < MAX_COLORS; i++) {
            const vec = material.uniforms.uColors.value[i];
            if (i < arr.length) vec.copy(arr[i]);
            else vec.set(0, 0, 0);
        }
        material.uniforms.uColorCount.value = arr.length;

        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        const renderer = new THREE.WebGLRenderer({
            antialias: false,
            powerPreference: 'high-performance',
            alpha: true
        });
        rendererRef.current = renderer;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setClearColor(0x000000, 0);
        renderer.domElement.style.width = '100%';
        renderer.domElement.style.height = '100%';
        renderer.domElement.style.display = 'block';
        container.appendChild(renderer.domElement);

        const clock = new THREE.Clock();
        const pointerTarget = pointerTargetRef.current;
        const pointerCurrent = pointerCurrentRef.current;

        const handleResize = () => {
            const w = container.clientWidth || 1;
            const h = container.clientHeight || 1;
            renderer.setSize(w, h, false);
            material.uniforms.uCanvas.value.set(w, h);
        };

        handleResize();

        if ('ResizeObserver' in window) {
            const ro = new ResizeObserver(handleResize);
            ro.observe(container);
            resizeObserverRef.current = ro;
        } else {
            window.addEventListener('resize', handleResize);
        }

        const handlePointerMove = e => {
            const rect = container.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / (rect.width || 1)) * 2 - 1;
            const y = -(((e.clientY - rect.top) / (rect.height || 1)) * 2 - 1);
            pointerTarget.set(x, y);
        };

        container.addEventListener('pointermove', handlePointerMove);

        const loop = () => {
            const dt = clock.getDelta();
            const elapsed = clock.elapsedTime;
            material.uniforms.uTime.value = elapsed;

            const deg = (rotationRef.current % 360) + autoRotateRef.current * elapsed;
            const rad = (deg * Math.PI) / 180;
            const c = Math.cos(rad);
            const s = Math.sin(rad);
            material.uniforms.uRot.value.set(c, s);

            const amt = Math.min(1, dt * pointerSmoothRef.current);
            pointerCurrent.lerp(pointerTarget, amt);
            material.uniforms.uPointer.value.copy(pointerCurrent);

            renderer.render(scene, camera);
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);

        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
            else window.removeEventListener('resize', handleResize);
            geometry.dispose();
            material.dispose();
            renderer.dispose();
            if (renderer.domElement && renderer.domElement.parentElement === container) {
                container.removeChild(renderer.domElement);
            }
        };
    }, [frequency, mouseInfluence, noise, parallax, scale, speed, transparent, warpStrength]);

    useEffect(() => {
        const material = materialRef.current;
        const renderer = rendererRef.current;
        if (!material) return;

        rotationRef.current = rotation;
        autoRotateRef.current = autoRotate;
        material.uniforms.uSpeed.value = speed;
        material.uniforms.uScale.value = scale;
        material.uniforms.uFrequency.value = frequency;
        material.uniforms.uWarpStrength.value = warpStrength;
        material.uniforms.uMouseInfluence.value = mouseInfluence;
        material.uniforms.uParallax.value = parallax;
        material.uniforms.uNoise.value = noise;

        const toVec3 = hex => {
            const h = hex.replace('#', '').trim();
            const v =
                h.length === 3
                    ? [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16)]
                    : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
            return new THREE.Vector3(v[0] / 255, v[1] / 255, v[2] / 255);
        };

        const arr = (colors || []).filter(Boolean).slice(0, MAX_COLORS).map(toVec3);
        for (let i = 0; i < MAX_COLORS; i++) {
            const vec = material.uniforms.uColors.value[i];
            if (i < arr.length) vec.copy(arr[i]);
            else vec.set(0, 0, 0);
        }
        material.uniforms.uColorCount.value = arr.length;

        material.uniforms.uTransparent.value = transparent ? 1 : 0;
        if (renderer) renderer.setClearColor(0x000000, transparent ? 0 : 1);
    }, [
        rotation,
        autoRotate,
        speed,
        scale,
        frequency,
        warpStrength,
        mouseInfluence,
        parallax,
        noise,
        colors,
        transparent
    ]);

    useEffect(() => {
        const material = materialRef.current;
        const container = containerRef.current;
        if (!material || !container) return;

        const handlePointerMove = e => {
            const rect = container.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / (rect.width || 1)) * 2 - 1;
            const y = -(((e.clientY - rect.top) / (rect.height || 1)) * 2 - 1);
            pointerTargetRef.current.set(x, y);
        };

        container.addEventListener('pointermove', handlePointerMove);
        return () => {
            container.removeEventListener('pointermove', handlePointerMove);
        };
    }, []);

    return <div ref={containerRef} className="color-bends-container" />;
}

// Main App Component
export default function App() {
    const [language, setLanguage] = useState('ar');

    const t = (key) => translations[language][key] || key;

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'ar' : 'en');
    };

    return (
        <div className={`app ${language === 'ar' ? 'rtl' : 'ltr'}`}>
            <ColorBends
                colors={['#6c06f1ff', '#1e109bff', '#610637ff', '#6b46f3ff']}
                speed={0.5}
                rotation={50}
                scale={1}
                frequency={1}
                warpStrength={1}
                mouseInfluence={1}
                parallax={0.5}
                noise={0.08}
                transparent={false}
            />
            <div className="language-switcher-wrapper">
                <button
                    className={`language-flag fixed-right ${language === 'ar' ? 'active' : ''}`}
                    onClick={() => setLanguage('ar')}
                >
                    🇸🇦
                </button>

                <button
                    className={`language-flag fixed-left ${language === 'en' ? 'active' : ''}`}
                    onClick={() => setLanguage('en')}
                >
                    🇺🇸
                </button>
            </div>

            <div className="container">
                <div className="profile-section">
                    <div className="profile-image-wrapper">
                        <img
                            src="https://res.cloudinary.com/dllzwh90f/image/upload/v1766563997/IMG_2287_1_nsxf6l.jpg"
                            className="profile-image"
                            alt="Profile"
                        />
                    </div>
                    <h1 className="profile-name">{t('name')}</h1>
                    <p className="profile-tagline">{t('tagline')}</p>
                </div>

                <div className="links-container">
                    <a href="tel:+966509739309" className="link-card">
                        <div className="link-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                        </div>
                        <div className="link-content">
                            <div className="link-title">{t('phone')}</div>
                            <div className="link-subtitle">{t('phoneDesc')}</div>
                        </div>
                        <div className="link-arrow">
                            <svg viewBox="0 0 24 24">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </div>
                    </a>

                    <a href="https://wa.me/+966509739309" className="link-card" target="_blank" rel="noopener noreferrer">
                        <div className="link-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            </svg>
                        </div>
                        <div className="link-content">
                            <div className="link-title">{t('whatsapp')}</div>
                            <div className="link-subtitle">{t('whatsappDesc')}</div>
                        </div>
                        <div className="link-arrow">
                            <svg viewBox="0 0 24 24">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </div>
                    </a>

                    <a href="https://linkedin.com/in/fahad-alshwihani" className="link-card" target="_blank" rel="noopener noreferrer">
                        <div className="link-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                                <rect x="2" y="9" width="4" height="12" />
                                <circle cx="4" cy="4" r="2" />
                            </svg>
                        </div>
                        <div className="link-content">
                            <div className="link-title">{t('linkedin')}</div>
                            <div className="link-subtitle">{t('linkedinDesc')}</div>
                        </div>
                        <div className="link-arrow">
                            <svg viewBox="0 0 24 24">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </div>
                    </a>

                    <a href="mailto:fhdalshammri34@gmail.com" className="link-card">
                        <div className="link-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                            </svg>
                        </div>
                        <div className="link-content">
                            <div className="link-title">{t('email')}</div>
                            <div className="link-subtitle">{t('emailDesc')}</div>
                        </div>
                        <div className="link-arrow">
                            <svg viewBox="0 0 24 24">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </div>
                    </a>

                    <a
                        href="https://github.com/FahadAlshwihani"
                        className="link-card"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <div className="link-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
                            </svg>
                        </div>

                        <div className="link-content">
                            <div className="link-title">{t('github')}</div>
                            <div className="link-subtitle">{t('githubDesc')}</div>
                        </div>

                        <div className="link-arrow">
                            <svg viewBox="0 0 24 24">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </div>
                    </a>



                    <a href="https://fyaa.io" className="link-card" target="_blank" rel="noopener noreferrer">
                        <div className="link-icon">
                            <svg viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="2" y1="12" x2="22" y2="12" />
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                            </svg>
                        </div>
                        <div className="link-content">
                            <div className="link-title">{t('website')}</div>
                            <div className="link-subtitle">{t('websiteDesc')}</div>
                        </div>
                        <div className="link-arrow">
                            <svg viewBox="0 0 24 24">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    );
}